import { useMemo } from 'react';

import { type UserProfile } from '#packages/data-collection/models/user-profile';
import { isNil } from '#packages/data-collection/libs/lodash';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';

import { type Auth } from '#apps/client-web/features/auth/hooks/use-auth';
import { useUserProfile } from '#apps/client-web/features/auth/hooks/use-user-profile';

export type UserProfilePageViewModel = ReturnType<
  typeof useUserProfilePageViewModel
>;

type LoadingState = {
  state: 'loading';
};

type GuestState = {
  state: 'guest';
  onSignIn: () => void;
};

type SignedInState = {
  state: 'signed-in';

  userProfile: UserProfile;
  onSignOut: () => void;
};

type SignedInNoProfileState = {
  state: 'signed-in-no-profile';
  onSignOut: () => void;
};

type ErrorState = {
  state: 'error';
  error: any;
};

export type PageState =
  | LoadingState
  | GuestState
  | SignedInState
  | SignedInNoProfileState
  | ErrorState;

export type UserProfilePageViewModelParams = LoggingContext & {
  auth: Auth;
};

export const useUserProfilePageViewModel = ({
  auth,

  logger,
}: UserProfilePageViewModelParams) => {
  const userProfile = useUserProfile(auth.user);

  const pageState = useMemo<PageState>(() => {
    logger?.debug({ auth, userProfile }, 'calculating page state');

    if (auth.isError) {
      return {
        state: 'error',
        error: auth.error,
      };
    }

    if (auth.isNotSignedIn) {
      return {
        state: 'guest',
        onSignIn: auth.onSignIn,
      };
    }

    if (auth.isSignedIn) {
      if (!isNil(userProfile)) {
        return {
          state: 'signed-in',
          userProfile,

          onSignOut: auth.onSignOut,
        };
      } else {
        return {
          state: 'signed-in-no-profile',
          onSignOut: auth.onSignOut,
        };
      }
    }

    // Fallback to loading state
    return {
      state: 'loading',
    };
  }, [
    auth.isLoading,
    auth.isError,
    auth.error,
    auth.isSignedIn,
    auth.isNotSignedIn,
    userProfile,
  ]);

  logger?.debug({ pageState }, 'Current Page State');

  return {
    page: pageState,
  };
};
